/**
 * Client-side helpers for vote_count / vote_average sort and min-vote filters
 * (TMDB list & search shapes).
 */

export type BrowseResultSortMode =
  | "relevance"
  | "vote_count_desc"
  | "vote_count_asc"
  | "vote_average_desc"
  | "vote_average_asc";

export function filterByMinVotes<T extends { vote_count?: number }>(
  items: T[],
  min: number,
): T[] {
  if (min <= 0) {
    return items;
  }
  return items.filter((item) => (item.vote_count ?? 0) >= min);
}

export function sortMediaByVoteCount<T extends { vote_count?: number }>(
  items: T[],
  direction: "desc" | "asc",
): T[] {
  const copy = [...items];
  copy.sort((a, b) => {
    const va = a.vote_count ?? 0;
    const vb = b.vote_count ?? 0;
    return direction === "desc" ? vb - va : va - vb;
  });
  return copy;
}

export function sortMediaByVoteAverage<T extends { vote_average?: number }>(
  items: T[],
  direction: "desc" | "asc",
): T[] {
  const copy = [...items];
  copy.sort((a, b) => {
    const va = a.vote_average ?? 0;
    const vb = b.vote_average ?? 0;
    return direction === "desc" ? vb - va : va - vb;
  });
  return copy;
}

export function applyBrowseDisplaySort<
  T extends { vote_count?: number; vote_average?: number },
>(items: T[], mode: BrowseResultSortMode): T[] {
  switch (mode) {
    case "relevance":
      return [...items];
    case "vote_count_desc":
      return sortMediaByVoteCount(items, "desc");
    case "vote_count_asc":
      return sortMediaByVoteCount(items, "asc");
    case "vote_average_desc":
      return sortMediaByVoteAverage(items, "desc");
    case "vote_average_asc":
      return sortMediaByVoteAverage(items, "asc");
    default:
      return [...items];
  }
}

const MIN_VOTE_PRESETS = ["", "50", "100", "500", "1000"] as const;

export function parseMinVotesParam(raw: string | null): number {
  const v = (raw || "").trim();
  if (!v || !MIN_VOTE_PRESETS.includes(v as (typeof MIN_VOTE_PRESETS)[number])) {
    return 0;
  }
  return Number(v) || 0;
}

export const MIN_VOTES_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "No minimum" },
  { value: "50", label: "At least 50 votes" },
  { value: "100", label: "At least 100 votes" },
  { value: "500", label: "At least 500 votes" },
  { value: "1000", label: "At least 1,000 votes" },
];

export const BROWSE_SORT_OPTIONS: { value: BrowseResultSortMode; label: string }[] =
  [
    { value: "relevance", label: "Default order" },
    { value: "vote_count_desc", label: "Most votes" },
    { value: "vote_count_asc", label: "Fewest votes" },
    { value: "vote_average_desc", label: "Highest rating" },
    { value: "vote_average_asc", label: "Lowest rating" },
  ];
